document.addEventListener('DOMContentLoaded', () => {
  if (!window.gsap) return;

  const PRM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.querySelector('.section-open-positions');
  if (!root) return;

  const OPEN_LABEL = 'See Less';
  const CLOSED_LABEL = 'Learn more';
  const TOP_OFFSET = 100;

  const cards = Array.from(root.querySelectorAll('.job-offer'));

  cards.forEach(card => {
    const content = card.querySelector('.job-offer__content');
    const btn = card.querySelector('[data-action="toggle-job-offer"]');
    if (!content || !btn) return;
    content.style.overflow = 'hidden';
    const isOpen = !content.hasAttribute('hidden');
    if (isOpen) {
      setContentMinHeight(content);
      gsap.set(content, { height: 'auto', opacity: 1, y: 0 });
      btn.setAttribute('aria-expanded', 'true');
      setButtonOpenState(btn, true);
      card.classList.add('active');
    } else {
      gsap.set(content, { height: 0, opacity: 0, y: -6 });
      btn.setAttribute('aria-expanded', 'false');
      setButtonOpenState(btn, false);
      card.classList.remove('active');
    }
  });

  let openCard = cards.find(c => c.classList.contains('active')) || null;

  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="toggle-job-offer"]');
    if (!btn) return;
    const card = btn.closest('.job-offer');
    if (!card) return;
    const contentId = btn.getAttribute('aria-controls');
    const content = contentId ? document.getElementById(contentId) : null;
    if (!content) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      closeCard(card, { focusButton: false });
      openCard = null;
    } else {
      if (openCard && openCard !== card) closeCard(openCard, { focusButton: false });
      openCard = card;
      openCardOnceTabs(content);
      openCardAnim(card, { afterOpenFocus: content });
    }
  });

  window.addEventListener('resize', () => {
    if (!openCard) return;
    const content = openCard.querySelector('.job-offer__content');
    if (content && !content.hasAttribute('hidden')) setContentMinHeight(content);
  });

  function openCardAnim(card, { afterOpenFocus }) {
    const btn = card.querySelector('[data-action="toggle-job-offer"]');
    const content = card.querySelector('.job-offer__content');
    if (!btn || !content) return;

    btn.setAttribute('aria-expanded', 'true');
    setButtonOpenState(btn, true);
    card.classList.add('active');
    content.removeAttribute('hidden');
    setContentMinHeight(content);

    if (PRM.matches) {
      content.style.height = 'auto';
      content.style.opacity = '1';
      content.style.transform = 'none';
      scrollToWithOffset(card, TOP_OFFSET, false);
      return;
    }

    gsap.killTweensOf(content);
    gsap.fromTo(
      content,
      { height: 0, opacity: 0, y: -6 },
      {
        height: 'auto',
        opacity: 1,
        y: 0,
        duration: 0.38,
        ease: 'power2.out',
        onComplete: () => {
          content.style.height = 'auto';
          scrollToWithOffset(card, TOP_OFFSET, true);
          const focusable = content.querySelector('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
          (focusable || afterOpenFocus || content).focus?.();
        }
      }
    );
  }

  function closeCard(card, { focusButton = false } = {}) {
    const btn = card.querySelector('[data-action="toggle-job-offer"]');
    const content = card.querySelector('.job-offer__content');
    if (!btn || !content) return;

    btn.setAttribute('aria-expanded', 'false');
    setButtonOpenState(btn, false);
    card.classList.remove('active');

    if (PRM.matches) {
      content.setAttribute('hidden', '');
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.transform = 'translateY(-6px)';
      content.style.minHeight = '';
      if (focusButton) btn.focus();
      return;
    }

    gsap.killTweensOf(content);
    gsap.to(content, {
      height: 0,
      opacity: 0,
      y: -6,
      duration: 0.3,
      ease: 'power2.inOut',
      onComplete: () => {
        content.setAttribute('hidden', '');
        content.style.minHeight = '';
        if (focusButton) btn.focus();
      }
    });
  }

  const initCache = new WeakSet();

  function openCardOnceTabs(contentEl) {
    if (initCache.has(contentEl)) return;
    initCache.add(contentEl);

    const tablist = contentEl.querySelector('[role="tablist"], .job-offer__tabs, .tabs__list, .tabs__nav');
    if (!tablist) return;

    const buttons = Array.from(
      tablist.querySelectorAll('[role="tab"], .tabs__btn, .job-offer__tabs__btn, .tab-btn')
    );
    const panelsRoot = contentEl;
    const panels = Array.from(
      panelsRoot.querySelectorAll('[role="tabpanel"], .tabs__panel, .job-offer__tabs__panel, .tab-panel')
    );
    if (!buttons.length || !panels.length) return;

    const byId = new Map(panels.map(p => [p.id, p]));
    let current = Math.max(0, buttons.findIndex(b => attrTrue(b, 'aria-selected') || b.classList.contains('active')));
    if (current === -1) current = 0;

    buttons.forEach((b, i) => {
      setTabState(b, i === current);
      const panel = resolvePanel(b, i);
      if (!panel) return;
      setPanelState(panel, i === current);
    });

    setContentMinHeight(contentEl);

    buttons.forEach((btn, i) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        activate(i, { focusPanel: true });
      });
      btn.addEventListener('keydown', (e) => {
        if (['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          let next = i;
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + buttons.length) % buttons.length;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % buttons.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = buttons.length - 1;
          buttons[next].focus();
          activate(next, { focusPanel: true });
        }
      });
    });

    function activate(index, { focusPanel = false } = {}) {
      if (index < 0 || index >= buttons.length) return;
      if (current === index) return;

      const prevBtn = buttons[current];
      const prevPanel = resolvePanel(prevBtn, current);
      const nextBtn = buttons[index];
      const nextPanel = resolvePanel(nextBtn, index);

      setTabState(prevBtn, false);
      if (prevPanel) setPanelState(prevPanel, false);
      setTabState(nextBtn, true);
      if (nextPanel) setPanelState(nextPanel, true, { animate: true });

      current = index;
    }

    function resolvePanel(btn, i) {
      const cid = btn.getAttribute('aria-controls');
      if (cid && byId.has(cid)) return byId.get(cid);
      return panels[i] || null;
    }

    function setTabState(btn, selected) {
      if (btn.hasAttribute('role')) {
        btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        btn.setAttribute('tabindex', selected ? '0' : '-1');
      }
      btn.classList.toggle('active', !!selected);
    }

    function setPanelState(panel, active, { animate = false } = {}) {
      if (panel.hasAttribute('role')) {
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      }
      if (!active) {
        panel.setAttribute('hidden', '');
        if (!PRM.matches && animate) gsap.set(panel, { autoAlpha: 0 });
        return;
      }
      panel.removeAttribute('hidden');
      if (PRM.matches || !animate) {
        gsap.set(panel, { clearProps: 'all' });
      } else {
        gsap.fromTo(panel, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: 0.24, ease: 'power2.out' });
      }
      const contentElRoot = panel.closest('.job-offer__content');
      if (contentElRoot) setContentMinHeight(contentElRoot);
    }

    function attrTrue(el, name) {
      const v = el.getAttribute(name);
      return v === '' || v === 'true';
    }
  }

  function setButtonOpenState(btn, open) {
    const labelEl = btn.querySelector('.body-semibold-3') || btn;
    labelEl.textContent = open ? OPEN_LABEL : CLOSED_LABEL;
    if (open) {
      btn.classList.remove('btn--v8');
      btn.classList.add('btn--v9');
    } else {
      btn.classList.remove('btn--v9');
      btn.classList.add('btn--v8');
    }
  }

  function setContentMinHeight(contentEl) {
    const panels = Array.from(
      contentEl.querySelectorAll('[role="tabpanel"], .tabs__panel, .job-offer__tabs__panel, .tab-panel')
    );
    if (!panels.length) {
      contentEl.style.minHeight = '';
      return;
    }
    let maxH = 0;
    panels.forEach(p => {
      const wasHidden = p.hasAttribute('hidden');
      const prevStyle = p.getAttribute('style') || '';
      if (wasHidden) {
        p.removeAttribute('hidden');
        p.style.position = 'absolute';
        p.style.visibility = 'hidden';
        p.style.display = 'block';
        p.style.height = 'auto';
        p.style.width = '100%';
      }
      const h = p.scrollHeight;
      if (h > maxH) maxH = h;
      if (wasHidden) {
        p.setAttribute('hidden', '');
        p.setAttribute('style', prevStyle);
      }
    });
    contentEl.style.minHeight = maxH ? `${maxH}px` : '';
  }

  function scrollToWithOffset(el, offset, smooth) {
    const y = window.scrollY + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top: y, behavior: smooth && !PRM.matches ? 'smooth' : 'auto' });
  }
});
